import React, { useEffect, useState } from "react";
import api from "../../api";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setSelectedMenu } from "../../redux/sidebarSlice";
import { showSuccessToast } from "../../utils/toastUtils";
import ChartInfoPopup from "./ChartInfoPopup";

const AllNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Today"); // "All", "Today", "Yesterday"

  const roleName = localStorage.getItem("role_name");

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ✅ Menu click handler
  const handleMenuClick = (label, path) => {
    // Update selected menu in Redux
    dispatch(setSelectedMenu(label));

    // Conditional navigation based on role
    switch (roleName?.toLowerCase()) {
      case "superadmin":
        navigate(`/superadmin${path}`);
        break;
      case "admin":
        navigate(`/admin${path}`);
        break;
      case "user":
        navigate(`/user${path}`);
        break;
      default:
        navigate(path); // fallback
    }

    // Show success notification
    showSuccessToast(`${label} opened successfully!`);
  };

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const baseUrl = process.env.REACT_APP_API_URL;

        const res = await api.get(`${baseUrl}/notifications/all`, config);

        const formattedNotifications = res.data.map((n) => ({
          id: n.notification_id,
          message: n.notification_message,
          read: n.read_status,
          createdAt: new Date(n.created_timestamp),
        }));

        // Sort by newest first
        formattedNotifications.sort((a, b) => b.createdAt - a.createdAt);

        setNotifications(formattedNotifications);
        setLoading(false);
      } catch (err) {
        console.error("❌ Failed to fetch notifications:", err);
        setLoading(false);
      }
    };

    fetchNotifications();
  }, []);

  // Filter notifications by tab
  const filterNotifications = () => {
    const now = new Date();
    return notifications.filter((note) => {
      const noteDate = note.createdAt;
      const isToday = noteDate.toDateString() === now.toDateString();
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      const isYesterday = noteDate.toDateString() === yesterday.toDateString();

      if (activeTab === "All") return true;
      if (activeTab === "Today") return isToday;
      if (activeTab === "Yesterday") return isYesterday;
      return true;
    });
  };

  const displayedNotifications = filterNotifications();

  // Detect links and make them clickable
  const renderMessage = (message) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return message.split(urlRegex).map((part, i) => {
      if (urlRegex.test(part)) {
        return (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#0d6efd",
              fontWeight: 500,
              textDecoration: "none",
            }}
            onMouseOver={(e) => (e.target.style.textDecoration = "underline")}
            onMouseOut={(e) => (e.target.style.textDecoration = "none")}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  return (
    <div className="profile d-flex flex-column p-4" style={{ height: "100%" }}>
      <div className="d-flex align-items-center mb-4 refresh-prompts">
        {/* <h5 className="fw-semibold refresh-prompts">
          <span className="border-start border-4 border-dark refresh-prompts ps-2">
            Notifications / Alerts
          </span>
        </h5> */}
        <div className="d-flex align-items-center gap-2">
          <h5 className="fw-semibold m-0">
            <span className="border-start border-4 border-dark ps-2">
              Notifications / Alerts
            </span>
          </h5>

          <div className="chart-info-anchor">
            <ChartInfoPopup text="Displays real-time updates, warnings, or important messages to keep users informed of platform events and activities." />
          </div>
        </div>
      </div>
      <ul style={{ fontSize: "small" }} className="nav nav-tabs mb-3">
        {["Today", "Yesterday", "All"].map((tab) => (
          <li className="nav-item" key={tab}>
            <button
              className={`nav-link ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          </li>
        ))}
      </ul>

      {/* ✅ Scrollable notifications container */}
      <div
        style={{
          maxHeight: "250px", // adjust height as needed
          overflowY: "auto",
        }}
      >
        {loading ? (
          <div style={{ fontSize: "small" }} className="text-center py-3">
            Loading notifications...
          </div>
        ) : displayedNotifications.length === 0 ? (
          <div
            style={{ fontSize: "small" }}
            className="text-center refresh-prompts py-3"
          >
            No notifications found
          </div>
        ) : (
          <ul className="list-group list-group-flush">
            {displayedNotifications.map((note) => (
              <li
                key={note.id}
                className="list-group-item px-3 py-3 border rounded mb-2 shadow-sm"
                style={{
                  backgroundColor: "#fff", // ✅ same white background for all
                  fontSize: "clamp(13px, 1.5vw, 15px)",
                  lineHeight: "1.5",
                  transition: "background 0.3s ease",
                  fontSize: "small",
                }}
              >
                <div
                  className="d-flex justify-content-between align-items-start"
                  style={{
                    overflowX: "auto", // ✅ horizontal scroll if content is large
                  }}
                >
                  <div style={{ flex: 1 }}>{renderMessage(note.message)}</div>
                </div>
                <div
                  className="text-muted mt-2"
                  style={{ fontSize: "12px", wordBreak: "break-word" }}
                >
                  {note.createdAt.toLocaleString()}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="text-end mt-2">
        <a
          style={{ fontSize: "small" }}
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleMenuClick("Notifications", "/notifications");
          }}
          className="fw-semibold"
        >
          More Items
        </a>
      </div>
    </div>
  );
};

export default AllNotifications;
